// Screenshot capture utility for ADA testing
// Provides fallback methods for capturing website screenshots

export class ScreenshotCapture {
  constructor() {
    this.isHtml2CanvasAvailable = typeof window !== 'undefined' && window.html2canvas;
  }

  // Create a placeholder screenshot with website preview
  createPlaceholderScreenshot(url, viewType = 'desktop', options = {}) {
    const width = viewType === 'mobile' ? 375 : 1200;
    const height = viewType === 'mobile' ? 667 : 800;
    
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Create modern gradient background
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(0.5, '#764ba2');
      gradient.addColorStop(1, '#f093fb');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Add subtle pattern overlay
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      for (let i = 0; i < width; i += 20) {
        for (let j = 0; j < height; j += 20) {
          if ((i + j) % 40 === 0) {
            ctx.fillRect(i, j, 2, 2);
          }
        }
      }
      
      // Add device frame
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 20, width - 40, height - 40);
      
      // Add text content
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      
      // Main title
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillText(`${viewType.toUpperCase()} PREVIEW`, width / 2, height / 2 - 60);
      
      // URL
      ctx.font = '16px Arial, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      const displayUrl = url.length > 40 ? url.substring(0, 37) + '...' : url;
      ctx.fillText(displayUrl, width / 2, height / 2 - 20);
      
      // Timestamp
      ctx.font = '14px Arial, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      const timestamp = new Date().toLocaleString();
      ctx.fillText(`Captured: ${timestamp}`, width / 2, height / 2 + 20);
      
      // Add device indicator
      if (viewType === 'mobile') {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.font = '12px Arial, sans-serif';
        ctx.fillText('📱 Mobile View (375x667)', width / 2, height / 2 + 50);
      } else {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.font = '12px Arial, sans-serif';
        ctx.fillText('🖥️ Desktop View (1200x800)', width / 2, height / 2 + 50);
      }
      
      // Add accessibility indicator
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.font = '12px Arial, sans-serif';
      ctx.fillText('🔍 ADA Compliance Testing', width / 2, height - 40);
      
      const screenshot = canvas.toDataURL('image/png');
      resolve(screenshot);
    });
  }

  // Attempt to capture real screenshot using iframe (limited by CORS)
  async captureViaIframe(url, viewType = 'desktop') {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      const width = viewType === 'mobile' ? 375 : 1200;
      const height = viewType === 'mobile' ? 667 : 800;
      
      iframe.style.width = `${width}px`;
      iframe.style.height = `${height}px`;
      iframe.style.border = 'none';
      iframe.style.position = 'absolute';
      iframe.style.left = '-9999px';
      iframe.style.top = '-9999px';
      
      // Set longer timeout for loading
      let timeoutId;
      let resolved = false;
      
      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      };
      
      const resolveWithFallback = () => {
        if (!resolved) {
          resolved = true;
          cleanup();
          // Return placeholder if real capture fails
          this.createPlaceholderScreenshot(url, viewType).then(resolve);
        }
      };
      
      iframe.onload = () => {
        try {
          // Try to capture with html2canvas if available
          if (this.isHtml2CanvasAvailable && iframe.contentDocument) {
            window.html2canvas(iframe.contentDocument.body, {
              width: width,
              height: height,
              useCORS: true,
              allowTaint: true
            }).then(canvas => {
              if (!resolved) {
                resolved = true;
                cleanup();
                resolve(canvas.toDataURL('image/png'));
              }
            }).catch(() => {
              resolveWithFallback();
            });
          } else {
            // Fallback to placeholder
            resolveWithFallback();
          }
        } catch (error) {
          resolveWithFallback();
        }
      };
      
      iframe.onerror = () => {
        resolveWithFallback();
      };
      
      // Set timeout for loading
      timeoutId = setTimeout(() => {
        resolveWithFallback();
      }, 5000); // 5 second timeout
      
      document.body.appendChild(iframe);
      iframe.src = url;
    });
  }

  // Main capture method with fallbacks
  async captureScreenshot(url, viewType = 'desktop') {
    try {
      // First try iframe capture (works for same-origin or CORS-enabled sites)
      return await this.captureViaIframe(url, viewType);
    } catch (error) {
      console.warn('Screenshot capture failed, using placeholder:', error);
      // Always fallback to placeholder
      return await this.createPlaceholderScreenshot(url, viewType);
    }
  }

  // Capture both desktop and mobile screenshots
  async captureMultipleViews(url) {
    const [desktop, mobile] = await Promise.all([
      this.captureScreenshot(url, 'desktop'),
      this.captureScreenshot(url, 'mobile')
    ]);

    return {
      desktop,
      mobile,
      timestamp: new Date().toISOString()
    };
  }
}

// Export singleton instance
export const screenshotCapture = new ScreenshotCapture();

// Legacy compatibility
export const captureScreenshot = (url, viewType) => screenshotCapture.captureScreenshot(url, viewType);